import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const PORTRAIT = "https://customer-assets-4nw71qhi.emergentagent.net/job_pm-portfolio-61/artifacts/t1bwqt0e_profile_photo.png";

// Line-by-line masked reveal
const Line = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Parallax
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const portraitScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden spotlight"
      data-testid="hero-section"
    >
      {/* Corner meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute top-24 left-6 md:left-10 z-10 font-mono text-[9px] md:text-[10px] tracking-[0.22em] md:tracking-[0.25em] uppercase text-[var(--bone-dim)] max-w-[45%]"
      >
        <div>[ 01 · Introduction ]</div>
        <div className="mt-1 text-[var(--bone)]/60">S 33.8688° · E 151.2093°</div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute top-24 right-6 md:right-10 z-10 font-mono text-[9px] md:text-[10px] tracking-[0.22em] md:tracking-[0.25em] uppercase text-[var(--bone-dim)] text-right max-w-[45%]"
      >
        <div className="text-[var(--copper)]">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--copper)] mr-2 blink" />
          Portfolio · PM
        </div>
        <div className="mt-1">Est. 2016 — Present</div>
      </motion.div>

      {/* Background type layer */}
      <motion.div
        style={{ y: gridY }}
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
      >
        <div className="font-serif italic text-[38vw] leading-none text-white/[0.014] select-none whitespace-nowrap">
          preeti
        </div>
      </motion.div>

      {/* Content grid */}
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 pt-40 md:pt-44 pb-24 grid grid-cols-12 gap-6 items-end min-h-[100svh]">
        {/* Left text */}
        <motion.div style={{ y: textY }} className="col-span-12 md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9 }}
            className="mb-8 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-[var(--copper)]" />
            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-[var(--copper)]">
              Payments · Product · Manager
            </span>
          </motion.div>

          <h1
            className="font-serif font-light text-[11vw] md:text-[6.6vw] leading-[0.98] text-[var(--bone)] tracking-tight"
            data-testid="hero-heading"
          >
            <Line delay={0.15}>
              Shipping money,
            </Line>
            <Line delay={0.35}>
              <span className="italic text-[var(--copper)]">gracefully.</span>
            </Line>
          </h1>

          <div className="mt-10 md:mt-14 grid grid-cols-12 gap-6 max-w-3xl">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.9 }}
              className="col-span-12 md:col-span-8 text-[var(--bone)]/75 text-lg md:text-xl leading-relaxed font-light"
              data-testid="hero-subtext"
            >
              I build payment products that move real money for real people —
              wallets, merchant rails, launch pads and evaluation tooling for
              the age of AI. Currently redefining trust at the edge of
              fintech and machine intelligence.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8, duration: 0.9 }}
              className="col-span-12 md:col-span-4 border-l border-[var(--bone)]/15 pl-6"
            >
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[var(--bone-dim)] mb-2">
                Now
              </div>
              <div className="text-[var(--bone)] text-sm leading-relaxed">
                Building AI evaluation
                <br />
                infrastructure for
                <br />
                payment platforms.
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.0, duration: 0.8 }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <a
              href="#work"
              data-testid="hero-cta-work"
              className="group inline-flex items-center gap-4 px-6 py-4 rounded-full bg-[var(--bone)] text-[var(--ink)] hover:bg-[var(--copper)] transition-colors"
            >
              <span className="font-mono text-[11px] tracking-[0.24em] uppercase">
                Selected Work
              </span>
              <span className="w-6 h-6 rounded-full bg-[var(--ink)] text-[var(--bone)] flex items-center justify-center group-hover:rotate-45 transition-transform">
                →
              </span>
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="font-mono text-[11px] tracking-[0.24em] uppercase link-line text-[var(--bone-dim)] hover:text-[var(--bone)]"
            >
              or, say hello ↗
            </a>
          </motion.div>
        </motion.div>

        {/* Right portrait */}
        <div className="col-span-12 md:col-span-5 relative">
          <div className="relative aspect-[4/5] md:aspect-[4/5.4] w-full max-w-md ml-auto">
            {/* Rotating badge */}
            <div className="absolute -top-14 -left-14 z-20 w-32 h-32 rounded-full border border-[var(--copper)]/40 flex items-center justify-center hidden md:flex">
              <svg viewBox="0 0 100 100" className="w-full h-full spin-slow absolute inset-0">
                <defs>
                  <path
                    id="circle"
                    d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0"
                  />
                </defs>
                <text className="fill-[var(--copper)] font-mono" fontSize="7.5" letterSpacing="2.2">
                  <textPath href="#circle">
                    PAYMENTS · PRODUCT · STRATEGY · AI · 
                  </textPath>
                </text>
              </svg>
              <span className="text-[var(--copper)] text-xl">✦</span>
            </div>

            {/* Frame border */}
            <div className="absolute -inset-3 border border-[var(--bone)]/10" />
            <div className="absolute -inset-6 border border-[var(--bone)]/5" />

            <motion.div
              style={{ y: portraitY, scale: portraitScale }}
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.4, delay: 0.9, ease: [0.7, 0, 0.2, 1] }}
              className="relative w-full h-full overflow-hidden bg-[var(--ink-2)]"
              data-testid="hero-portrait"
            >
              <img
                src={PORTRAIT}
                alt="Preeti Basu"
                className="w-full h-full object-cover object-top mix-blend-luminosity opacity-90 contrast-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/60 via-transparent to-transparent" />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(60% 50% at 55% 40%, transparent 0%, rgba(10,10,11,0.35) 100%)",
                }}
              />
            </motion.div>

            {/* Corner ticks */}
            <div className="absolute -top-1 -left-1 w-4 h-4 border-t border-l border-[var(--copper)]" />
            <div className="absolute -top-1 -right-1 w-4 h-4 border-t border-r border-[var(--copper)]" />
            <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b border-l border-[var(--copper)]" />
            <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b border-r border-[var(--copper)]" />

            {/* Caption */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.1, duration: 0.8 }}
              className="mt-4 flex items-center justify-between"
            >
              <span className="font-mono text-[8px] tracking-[0.2em] uppercase text-[var(--bone-dim)]">
                Fig. 01 — Portrait, Sydney
              </span>
              <span className="font-mono text-[8px] tracking-[0.2em] uppercase text-[var(--copper)]">
                / 2026
              </span>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--bone-dim)]">
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[var(--bone-dim)] to-transparent"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
